import {
  getBookingPageStatus,
  type UnavailableBookingPageStatus,
} from '@/helpers/tenant';
import { getErrorCode } from './error-utils';

// Two site-wide states and nothing else. A provider refusal, an ownership 403,
// a 404, a 429 and every ordinary failed read are decided by the screen that
// asked for them, so none of them belongs here.
export type SiteFailure =
  | { kind: 'dark'; reason: UnavailableBookingPageStatus }
  | { kind: 'unreachable' };

// A definite answer outranks the absence of one, and a suspension outranks a
// not-live page because a preview token bypasses one and not the other. Only
// one value is held, so a later failure must not displace a higher-ranked one.
const rank = (failure: SiteFailure) =>
  failure.kind === 'unreachable' ? 0 : failure.reason === 'not-live' ? 1 : 2;

let current: SiteFailure | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const setFailure = (next: SiteFailure) => {
  if (current && rank(next) <= rank(current)) return;
  current = next;
  notify();
};

// Classified on the backend's code, never on the status. Two endpoints can
// never reach this: /public/users/me, whose query is marked isSessionCheck and
// skipped by the cache handler in query-client.ts, and /public/tracking, which
// is a bare post rather than a query.
export const recordQueryFailure = (error: unknown) => {
  const status = getBookingPageStatus(error);
  if (status !== 'available') {
    setFailure({ kind: 'dark', reason: status });
  } else if (getErrorCode(error) === 'PROXY_UPSTREAM_UNAVAILABLE') {
    setFailure({ kind: 'unreachable' });
  }
};

// Never latches: without this one transient blip keeps a studio dark for the
// life of the tab, and the only way back is a hard reload.
export const clearSiteFailure = () => {
  if (!current) return;
  current = null;
  notify();
};

export const subscribeToSiteFailure = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getSiteFailure = () => current;

// One Node process serves every studio at once, so a server render must never
// read a value the browser wrote for somebody else.
export const getServerSiteFailure = (): SiteFailure | null => null;
